import { AsyncPipe } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, HostBinding, Input, OnChanges, OnInit, SimpleChanges, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ScoreboardFactory } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { EventModel, LeaderboardGameScore } from '@frontend/sports/betting-offer/feature/model';
import { FavouritesConfig, LiveSectionConfig, Sitecore, SitecoreGameMessagesConfig } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { DeviceService, MediaQueryService } from '@frontend/vanilla/core';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { Observable, filter, map } from 'rxjs';

import { BetBuilderConfigService } from '../betbuilder/services/betbuilder-config.service';
import { isBetbuilderEvent } from '../betbuilder/utils';
import { EpcotConfigService } from '../common/epcot-config.service';
import { BadgeComponent } from '../crm-promotion/badge.component';
import { CrmPromotionProviderService, PromotionMap } from '../crm-promotion/crm-promotion-provider.service';
import { IconSize } from '../event-list/event-list.models';
import { FavouriteEventToggleComponent } from '../favourites/toggle/favourite-event-toggle.component';
import { EventTimerComponent } from '../timers/event-timer.component';
import { TreeEventNameComponent } from './tree-event-name.component';
import { TreeRaceInfoComponent } from './tree-race-info.component';

@Component({
    selector: 'ms-tree-event-detail',
    templateUrl: 'tree-event-detail.component.html',
    imports: [
        TreeEventNameComponent,
        FavouriteEventToggleComponent,
        TreeRaceInfoComponent,
        IconCustomComponent,
        EventTimerComponent,
        BadgeComponent,
        AsyncPipe,
    ],
})
export class TreeEventDetailComponent implements OnInit, OnChanges {
    @Input() event: EventModel;
    @Input() hideFavouriteIcon?: boolean;
    @Input() updatedFavourites: string[] = [];
    @HostBinding('class') className = 'tree-event-detail';
    showInlineFavoriteToggle = false;
    crmPromoted = false;
    broadcaster$: Observable<string> | undefined;
    liveEventInfo?: string;
    size: IconSize;
    isMobile: boolean;
    epcotEnabled: boolean;
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        public sitecore: Sitecore,
        private favouritesConfig: FavouritesConfig,
        public deviceService: DeviceService,
        private crmPromotionProvider: CrmPromotionProviderService,
        private changeDetectorRef: ChangeDetectorRef,
        private betBuilderConfigService: BetBuilderConfigService,
        private sitecoreGameMessage: SitecoreGameMessagesConfig,
        private mediaObserver: MediaQueryService,
        private scoreboardFactory: ScoreboardFactory,
        private liveSectionConfig: LiveSectionConfig,
        private epcotConfigService: EpcotConfigService,
    ) {
        this.isMobile = this.deviceService.isMobile;
        this.epcotEnabled = this.epcotConfigService.isEnabled();
        this.setIconSize();
    }

    ngOnInit(): void {
        this.crmPromotionProvider.promotionsMap$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((ids) => this.promotionMapChanged(ids));
        this.showInlineFavoriteToggle = this.favouritesConfig.isLiveFavouritesEnabled && this.deviceService.isTouch && !this.hideFavouriteIcon;
        this.broadcaster$ = this.sitecoreGameMessage.whenReady.pipe(
            filter(() => !this.event?.scoreboard?.started && (this.mediaObserver.isActive('xs') || !this.showBetBuilderIcon)),
            map(() => (this.sitecoreGameMessage.secondaryBroadcastInfo?.messages ?? {})[this.event.id]),
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.event) {
            this.liveEventInfo = this.getLiveEventInfo(this.event.sport.id, this.event.league.parentLeagueId);
        }
    }

    get showBetBuilderIcon(): boolean {
        return !this.crmPromoted && isBetbuilderEvent(this.event, this.betBuilderConfigService);
    }

    showRaceInfo(): boolean {
        const score = this.event.scoreboard.getScore<LeaderboardGameScore>()!;

        return (
            this.event.scoreboard.started &&
            score &&
            score.type &&
            !score.preRace &&
            (this.event.sport.id === SportConstant.Formula1 || this.event.sport.id === SportConstant.Motorbikes)
        );
    }

    private promotionMapChanged(promoMap: PromotionMap): void {
        this.crmPromoted = !!promoMap.fixtures[this.event.id];
        this.changeDetectorRef.markForCheck();
    }

    private getLiveEventInfo(sportsId: number, leagueId?: number): string | undefined {
        if (!this.event.scoreboard.started || !leagueId || !this.liveSectionConfig.liveEventsInfoForLeagueIds.includes(leagueId)) {
            return;
        }

        return this.scoreboardFactory.getSportsLiveEventInfo(sportsId, this.event.scoreboard);
    }

    setIconSize(): void {
        this.size = this.epcotEnabled ? { height: '13px', width: '19px' } : { height: '9px', width: '13px' };
    }
}
